import { useEffect, useRef, type RefObject } from 'react';
import { useFocusTrap } from '@/lib/hooks';

interface UseDialogOptions {
    isOpen: boolean;
    onClose: () => void;
    closeOnEscape?: boolean;
}

// Composes useFocusTrap with Escape-to-close and focus-return-to-trigger.
// Returns a ref to attach to the focusable region. The caller controls
// rendering and animation.
export function useDialog({
    isOpen,
    onClose,
    closeOnEscape = true,
}: UseDialogOptions): RefObject<HTMLDivElement | null> {
    const ref = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<Element | null>(null);

    useFocusTrap(ref, isOpen);

    // Escape-to-close
    useEffect(() => {
        if (!isOpen || !closeOnEscape) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isOpen, closeOnEscape, onClose]);

    // Initial focus + focus return
    useEffect(() => {
        if (!isOpen) return;
        triggerRef.current = document.activeElement;
        const node = ref.current;
        if (!node) return;
        const first = node.querySelector<HTMLElement>(
            'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        (first ?? node).focus();
        return () => {
            const trigger = triggerRef.current;
            if (trigger instanceof HTMLElement) trigger.focus();
        };
    }, [isOpen]);

    return ref;
}
